import React from 'react';
import { useGetResize, UseGetResizePayload } from './useGetResize';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';

interface ResizerProps extends UseGetResizePayload {
  left: React.ReactNode;
  right: React.ReactNode;
  minLeftWidth: number | `${number}%`;
  maxLeftWidth: number | `${number}%`;
}

export const Resizer: React.FC<BpDesignSystem.Props<ResizerProps>> = ({
  left,
  right,
  minLeftWidth,
  maxLeftWidth,
  style,
  className,
  sx,
  dataTest,
  ...getResizeProps
}) => {
  const { handleMouseDown, handleReset, leftWidth, containerRef, isResizing } = useGetResize(getResizeProps);
  return (
    <StyledContainer ref={containerRef} sx={sx} style={style} className={className} data-test={dataTest}>
      <StyledLeftContainer $width={leftWidth} $minWidth={minLeftWidth} $maxWidth={maxLeftWidth}>
        {left}
      </StyledLeftContainer>
      <StyledResizerContainer>
        <StyledResizer $active={isResizing} onDoubleClick={handleReset} onMouseDown={handleMouseDown} />
      </StyledResizerContainer>
      <StyledRightContainer>{right}</StyledRightContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')`
  display: grid;
  grid-template-columns: auto 1px 1fr;
  grid-template-rows: 100%;
  align-items: stretch;
  justify-content: stretch;
  height: 100%;
  width: 100%;
  max-height: 100%;
  overflow: hidden;
`;

const StyledLeftContainer = styled('div')<{
  $width: number;
  $minWidth: number | `${number}%`;
  $maxWidth: number | `${number}%`;
}>`
  width: ${({ $width }) => $width}px;
  min-width: ${({ $minWidth }) => (typeof $minWidth === 'string' ? $minWidth : `${$minWidth}px`)};
  max-width: ${({ $maxWidth }) => (typeof $maxWidth === 'string' ? $maxWidth : `${$maxWidth}px`)};
`;

const StyledRightContainer = styled('div')``;

const StyledResizerContainer = styled('div')`
    display: flex;
    justify-content: center;
    flex-flow: column;
    height: 100%;
`;

const StyledResizer = styled('div')<{
  $active: boolean;
}>`
  position: relative;
  transition: background-color 0.2s ease-in-out;
  background-color: ${({ $active, theme }) => ($active ? theme.tokens.colorPrimary : theme.tokens.colorSurfaceVariant)};
  cursor: col-resize;
  height: 100%;
  width: 1px;

  /* Widen the drag target beyond the 1px visual line without affecting layout. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 -4px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.tokens.colorPrimary};
  }
`;
